import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { BC_FIELDS } from '@/shared/constants/bowlingCenterFields';
import { queryKeys } from '@/shared/constants/queryKeys';

import { getBowlingCentersByUuids } from '@/services/home';

// Fetches recent bowling centers by their UUIDs
function useRecentCentersData(uuids) {
  // Sort UUIDs and memoize to ensure stable query key (same UUIDs = same cache key)
  const sortedUuids = useMemo(() => {
    if (!Array.isArray(uuids) || uuids.length === 0) return [];
    return [...uuids].sort();
  }, [uuids]);

  const { data, isPending, isError, error } = useQuery({
    queryKey: [queryKeys.allBowlingCenters, 'recents', sortedUuids],
    queryFn: () =>
      getBowlingCentersByUuids(sortedUuids, {
        fields: BC_FIELDS.join(','),
      }),
    select: (response) => {
      const centers = response?.data?.data ?? response?.data ?? [];
      return centers.map(({ countryCode, ...bc }) => ({
        ...bc,
        countryFlag: `https://flagcdn.com/w20/${countryCode}.jpg`,
      }));
    },
    enabled: sortedUuids.length > 0,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnMount: false, // Don't refetch when component mounts if data exists
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  return {
    data,
    isLoading: isPending,
    isError,
    error,
  };
}

export default useRecentCentersData;
